import {
  ChamberMemberProPaymentScheduleEnum,
  CmpInvoiceKindEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpInvoiceSourceFilterEnum,
  CmpManageInvoiceRowFieldsFragment,
} from "../../../../graphql/types";

type ManageInvoiceLabelRow = Pick<
  CmpManageInvoiceRowFieldsFragment,
  "invoiceKind" | "paymentPlan" | "paymentRules" | "sourceLabel" | "totalAmountInCents"
> & Partial<Pick<CmpManageInvoiceRowFieldsFragment, "sponsorshipLevelLabel" | "ticketTypeLabel">>;

const isMembershipKind = (invoiceKind: CmpInvoiceKindEnum) =>
  invoiceKind === CmpInvoiceKindEnum.Application ||
  invoiceKind === CmpInvoiceKindEnum.Renewal;

/**
 * Short Type column label from the Claude Design invoice table. The long
 * source label (for example "Event Sponsorship: Golf Day") stays on the
 * invoice document. Sponsorship rows show the saved level name (for example
 * "Gold Sponsorship"), with the design fallback "Sponsorship".
 */
export const getManageInvoiceTypeLabel = (row: ManageInvoiceLabelRow): string => {
  switch (row.invoiceKind) {
    case CmpInvoiceKindEnum.Application:
    case CmpInvoiceKindEnum.Renewal:
      return "Company Membership";
    case CmpInvoiceKindEnum.EventRegistration:
      // IF11 EV3 (Q5, design CMPD:14291): the exact ticket type names; a $0
      // complimentary sale keeps its real ticket type. Older rows without
      // the field keep the label rules below.
      if (row.ticketTypeLabel?.trim()) {
        return row.ticketTypeLabel.trim();
      }
      if (row.totalAmountInCents === 0) {
        return "Complimentary Ticket";
      }
      if (
        row.paymentRules.ruleKind ===
        CmpInvoicePaymentRuleKindEnum.EventNonMemberTickets
      ) {
        return "Non-Member Ticket";
      }
      if (
        row.paymentRules.ruleKind ===
        CmpInvoicePaymentRuleKindEnum.EventMemberTickets
      ) {
        return "Member Ticket";
      }
      return "Event Ticket";
    case CmpInvoiceKindEnum.EventSponsorship:
      return row.sponsorshipLevelLabel?.trim() || "Sponsorship";
    case CmpInvoiceKindEnum.Advertising:
      return "Advertising";
    case CmpInvoiceKindEnum.Custom:
      return "Custom Invoice";
    default:
      return row.sourceLabel.split(":")[0]?.trim() || "-";
  }
};

// Membership-related invoices carry a billing plan: application, renewal and
// add-on benefits (design (21) CLAUDE.md "Billing method on non-membership
// invoices").
const hasMembershipBillingPlan = (invoiceKind: CmpInvoiceKindEnum) =>
  isMembershipKind(invoiceKind) || invoiceKind === CmpInvoiceKindEnum.AddOn;

/**
 * Billing Method column: the billing plan, not the payment choice. Design (21)
 * CMP Dashboard.dc.html:11394-11397: membership invoices show Monthly / Annual
 * / Chamber Insurance; every other invoice (tickets, sponsorships, advertising,
 * custom) shows One-Time (Monday 13330402194).
 */
export const getManageInvoiceBillingPlanLabel = (
  row: ManageInvoiceLabelRow,
): string => {
  if (
    row.paymentRules.ruleKind ===
    CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly
  ) {
    return "Chamber Insurance";
  }

  if (
    row.paymentRules.ruleKind ===
      CmpInvoicePaymentRuleKindEnum.MonthlyCreditCardOnly ||
    (row.paymentPlan?.enabled &&
      row.paymentPlan.paymentSchedule ===
        ChamberMemberProPaymentScheduleEnum.Monthly)
  ) {
    return "Monthly";
  }

  return hasMembershipBillingPlan(row.invoiceKind) ? "Annual" : "One-Time";
};

// Claude Design DASH invoiceTypeOptions, in design order. Each option maps to
// the backend source filter that contains it. Ticket options use the backend
// ticket split (IF11 EV3): sold to a member, sold to a non-member, or a
// staff-issued complimentary ticket. The design's sample levels
// (Title/Gold/Silver/Bronze) are not real chamber data, so one "Sponsorship"
// option covers every level.
export const manageInvoiceTypeFilterOptions: ReadonlyArray<{
  label: string;
  source: CmpInvoiceSourceFilterEnum;
}> = [
  { label: "Company Membership", source: CmpInvoiceSourceFilterEnum.Membership },
  { label: "Member Ticket", source: CmpInvoiceSourceFilterEnum.EventTicketMember },
  { label: "Non-Member Ticket", source: CmpInvoiceSourceFilterEnum.EventTicketNonMember },
  { label: "Complimentary Ticket", source: CmpInvoiceSourceFilterEnum.EventTicketComplimentary },
  { label: "Advertising", source: CmpInvoiceSourceFilterEnum.Advertising },
  { label: "Custom Invoice", source: CmpInvoiceSourceFilterEnum.Custom },
  { label: "Sponsorship", source: CmpInvoiceSourceFilterEnum.EventSponsorship },
];

export const getManageInvoiceTypeSourceFilters = (
  selectedLabels: readonly string[],
): CmpInvoiceSourceFilterEnum[] =>
  Array.from(
    new Set(
      manageInvoiceTypeFilterOptions
        .filter((option) => selectedLabels.includes(option.label))
        .map((option) => option.source),
    ),
  );

// Claude Design DASH billingMethodOptions, in design order.
// Design (21) CMP Dashboard.dc.html:18953 billingMethodOptions, in order.
export const manageInvoiceBillingMethodFilterOptions = [
  "One-Time",
  "Credit Card",
  "Company Cheque",
  "E-Transfer",
  "Cash",
  "Direct Deposit",
  "Chamber Group Insurance",
  "Monthly (Auto-Renew)",
  "Annual (Auto-Renew)",
] as const;

export type ManageInvoiceBillingMethodFilterOption =
  (typeof manageInvoiceBillingMethodFilterOptions)[number];

const normalizeBillingLabel = (label: string) =>
  label.toLowerCase().replace(/[^a-z]/g, "");

// The backend filters by the exact row billing label (a recorded payment
// method, "Monthly", "Chamber Group / Insurance", or "Credit Card"). Each
// design option sends its canonical backend label plus every equivalent label
// that the backend reported for this chamber. Combined unpaid labels such as
// "Invoice Me / Company Cheque" are not matched, like the design, and stay
// visible under "All".
// The plan options (One-Time, Chamber Group Insurance, Monthly and Annual)
// send a "plan:" filter that CAP matches by billing plan (Moeen IF11 Q44), so
// they match the Billing Method column; the payment options match the
// recorded payment method.
const billingMethodServerLabels: Record<
  ManageInvoiceBillingMethodFilterOption,
  { canonical: readonly string[]; equivalents: readonly string[] }
> = {
  "One-Time": { canonical: ["plan:ONE_TIME"], equivalents: [] },
  "Credit Card": { canonical: ["Credit Card"], equivalents: ["creditcard"] },
  "Company Cheque": {
    canonical: ["Company Cheque", "Cheque"],
    equivalents: ["companycheque", "cheque", "companycheck", "check"],
  },
  "E-Transfer": {
    canonical: ["E-Transfer"],
    equivalents: ["etransfer", "interacetransfer", "emailtransfer"],
  },
  Cash: { canonical: ["Cash"], equivalents: ["cash"] },
  "Direct Deposit": {
    canonical: ["Direct Deposit"],
    equivalents: ["directdeposit"],
  },
  "Chamber Group Insurance": { canonical: ["plan:INSURANCE"], equivalents: [] },
  "Monthly (Auto-Renew)": { canonical: ["plan:MONTHLY"], equivalents: [] },
  "Annual (Auto-Renew)": { canonical: ["plan:ANNUAL"], equivalents: [] },
};

export const getManageInvoiceBillingServerFilters = ({
  knownServerLabels,
  selectedOptions,
}: {
  knownServerLabels: readonly string[];
  selectedOptions: readonly string[];
}): string[] => {
  const labels = new Set<string>();

  manageInvoiceBillingMethodFilterOptions.forEach((option) => {
    if (!selectedOptions.includes(option)) {
      return;
    }

    const { canonical, equivalents } = billingMethodServerLabels[option];
    canonical.forEach((label) => labels.add(label));
    knownServerLabels.forEach((label) => {
      if (label !== "all" && equivalents.includes(normalizeBillingLabel(label))) {
        labels.add(label);
      }
    });
  });

  return Array.from(labels).sort();
};
